import { createContext, useContext, useEffect, useState } from 'react';
import { LLMPatientInformation } from './LLMDiagnoseContext';

type Session = {
  created_at: string;
  updated_at: string;
  type: string;
  data: {
    patient: LLMPatientInformation;
    notes: string;
  };
  id: string;
};

type DiagnosticsComponentContextType = {
  sessionId: string;
  setSessionId: (sessionId: string) => void;
  session: Session | null;
  setSession: (session: Session | null) => void;
  signature: string;
  setSignature: (signature: string) => void;
  timestamp: string;
  setTimestamp: (timestamp: string) => void;
};

const DiagnosticsComponentContext =
  createContext<DiagnosticsComponentContextType>({
    sessionId: '',
    setSessionId: () => {},
    session: null,
    setSession: () => {},
    signature: '',
    setSignature: () => {},
    timestamp: '',
    setTimestamp: () => {},
  });

export const DiagnosticsComponentProvider = ({
  children,
}: React.PropsWithChildren) => {
  const [sessionId, setSessionId] = useState('');
  const [timestamp, setTimestamp] = useState('');
  const [signature, setSignature] = useState('');
  const [session, setSession] = useState<Session | null>(null);
  const value = {
    sessionId,
    setSessionId,
    session,
    setSession,
    timestamp,
    setTimestamp,
    signature,
    setSignature,
  };

  useEffect(() => {
    if (!sessionId) return;
    setSession(null);
    fetch(`/api/v1/sessions/${sessionId}`)
      .then((response) => response.json())
      .then((data) => {
        setSession(data);
        // Only set auth header if we have valid signature and timestamp (not dummy values)
        if (
          signature &&
          timestamp &&
          signature !== 'dummy' &&
          timestamp !== 'dummy'
        ) {
          window.sessionStorage.setItem(
            'gekko/authheader',
            `GekkoComponents ${sessionId};${signature};${timestamp}`
          );
        }
        (window as any).__componentsCallback('sessionLoaded', data);
      });
  }, [sessionId, signature, timestamp]);

  return (
    <DiagnosticsComponentContext.Provider value={value}>
      {children}
    </DiagnosticsComponentContext.Provider>
  );
};

export const useDiagnosticsComponentContext = () => {
  return useContext(DiagnosticsComponentContext);
};
